import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

interface DividerBGProps {
  /** Per-instance content namespace; pages.json supplies a unique value
   *  for each placement (e.g. `amenities-divider-bg`). Falls back to
   *  the canonical schema key segment so the component stays usable
   *  standalone and the Home instance keeps its existing content. */
  tokenPrefix?: string;
  /** Hide the thin white border that frames the tagline. The frame's
   *  inner padding is preserved so the section height stays the same;
   *  only the 1px border is dropped. Defaults to `false` (frame shown),
   *  preserving the original Home-page look. The amenities page opts
   *  in (`hideFrame: true`) to drop the box around the divider. */
  hideFrame?: boolean;
  /** Hide the 40% black overlay that sits between the background photo
   *  and the tagline. Defaults to `false` (overlay shown), preserving
   *  the original Home-page look. The amenities page opts in
   *  (`hideOverlay: true`) so the underlying photo reads at full
   *  brightness. */
  hideOverlay?: boolean;
}

/**
 * Weidner Via6 DividerBG section.
 *
 * Mirrors the "Live your best life at Via6." full-bleed banner stripe
 * on via6living.com (the `div.u_1410493285` block). Layout:
 *
 *   ┌──────────────────────────────────────────────────────┐
 *   │  [background photo, cover, center]                   │
 *   │  [black overlay @ ~40% opacity]                      │
 *   │        ┌────────────────────────────────┐            │
 *   │        │  Live your best life at Via6.  │            │
 *   │        └────────────────────────────────┘            │
 *   └──────────────────────────────────────────────────────┘
 *
 * The thin white-bordered rectangle around the centered tagline
 * matches the `u_1101772183` inner column on via6living, which uses
 * `border:1px solid #fff` to frame the heading on top of the photo.
 *
 * No design tokens are referenced — the user wanted this stripe to
 * stand on its own and not pick up theme `--vibe-color-*` overrides.
 * Colors are hard-coded: white border / text, 40% black overlay.
 * The heading font is hard-coded to Prata (the via6living face) so it
 * stays consistent regardless of the host theme.
 *
 * Shared-component pattern (see reference/shared-component-pattern.md):
 * the component accepts a `tokenPrefix` prop so multiple instances on
 * different pages (home, amenities, ...) can keep independent editor
 * overrides. The Home page omits the prop and falls back to the
 * canonical `dividerbg` namespace (preserving existing content); new
 * instances pass a unique prefix in pages.json (e.g.
 * `amenities-divider-bg`).
 *
 * Editor surface:
 *   - `${prefix}.bg_image` — the background photo (slot group). Bound
 *     to `photo_gallery[0]` so each property auto-uses its own gallery
 *     image. No hardcoded CDN fallback — a Via6 Seattle asset previously
 *     leaked to every site on this base theme when gallery was empty.
 *   - `${prefix}.tagline` — the centered headline (richtext).
 */

const bgImageDef = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Background',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function DividerBG({ tokenPrefix, hideFrame, hideOverlay }: DividerBGProps = {}) {
  const prefix = tokenPrefix || 'dividerbg';
  const bgPath = `${prefix}.bg_image`;
  const taglinePath = `${prefix}.tagline`;

  const bg = useSlotGroup(bgPath, bgImageDef);

  return (
    <section
      className={[
        'dividerbg-section',
        hideFrame ? 'dividerbg-section--no-frame' : '',
        hideOverlay ? 'dividerbg-section--no-overlay' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Full-bleed background image */}
      <EditableSlotGroup groupPath={bgPath} definition={bgImageDef}>
        <div className="dividerbg-bg">
          {bg.image ? (
            <VibesitesImage
              url={bg.image}
              alt={bg.alt || ''}
              className="dividerbg-bg-img"
              loading="lazy"
              decoding="async"
            />
          ) : null}
        </div>
      </EditableSlotGroup>

      {/* Dark overlay for text contrast — matches via6living's 0.4 black.
          Suppressed when `hideOverlay` is set (e.g. the Amenities page),
          so the underlying photo reads at full brightness. */}
      {hideOverlay ? null : (
        <div className="dividerbg-overlay" aria-hidden="true" />
      )}

      {/* White-bordered frame containing the centered tagline */}
      <div className="dividerbg-content">
        <div className="dividerbg-frame">
          <RichTextContent
            path={taglinePath}
            as="h2"
            className="dividerbg-tagline"
          />
        </div>
      </div>

      {/* Schema-lint anchors — carry the canonical literal paths so the
          source linter can statically match `dividerbg.bg_image` and
          `dividerbg.tagline`. Never rendered visibly; never receive
          clicks. The visible wrappers above use the dynamic per-instance
          paths so the editor saves to the correct namespace. */}
      <EditableSlotGroup
        groupPath="dividerbg.bg_image"
        definition={bgImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <RichTextContent
        path="dividerbg.tagline"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />

      <style>{`
        .dividerbg-section {
          position: relative;
          width: 100%;
          overflow: hidden;
          isolation: isolate;
          /* Equal 28px inset on all four sides between the photo and the
             frame. No min-height — the section height is just frame +
             56px, so the gap stays identical on every side. */
          padding: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #000;
        }
        .dividerbg-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
        }
        .dividerbg-bg-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
        }
        .dividerbg-overlay {
          position: absolute;
          inset: 0;
          z-index: 1;
          background-color: rgba(0, 0, 0, 0.4);
          pointer-events: none;
        }
        .dividerbg-content {
          position: relative;
          z-index: 2;
          width: 100%;
          display: flex;
          justify-content: center;
        }
        /* The thin white-outlined "box" framing the heading on top of
           the image — mirrors via6living's u_1101772183 inner column.
           The 28px inset on all sides comes from .dividerbg-section's
           padding, so the frame just fills the content area. Vertical
           padding here is what drives the overall section height. */
        .dividerbg-frame {
          border: 1px solid #fff;
          padding: 3.5rem 1.75rem;
          width: 100%;
          max-width: none;
          text-align: center;
        }
        /* Frame-off variant — the "white box" is dropped (used on
           /amenities, where the divider sits between two image rows
           and the extra border felt over-framed). Padding is left
           alone so the section keeps its current height. */
        .dividerbg-section--no-frame .dividerbg-frame {
          border: none;
        }
        @media (min-width: 768px) {
          .dividerbg-frame {
            padding: 4.5rem 3rem;
          }
        }
        @media (min-width: 1024px) {
          .dividerbg-frame {
            padding: 5.5rem 4rem;
          }
        }
        .dividerbg-tagline {
          margin: 0;
          font-family: 'Prata', serif;
          font-weight: 400;
          color: #fff;
          line-height: 1.15;
          letter-spacing: -0.01em;
          font-size: 2rem;
          text-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
        }
        @media (min-width: 768px) {
          .dividerbg-tagline {
            font-size: 2.75rem;
          }
        }
        @media (min-width: 1024px) {
          .dividerbg-tagline {
            font-size: 3.5rem;
          }
        }
        .dividerbg-tagline :is(p, h1, h2, h3) {
          margin: 0;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'DividerBG',
  displayName: 'Divider Background Banner',
  tokens: {
    'dividerbg.tagline': {
      label: 'Tagline',
      type: 'richtext',
      defaultValue: '<p>Live your best life at Via6.</p>',
    },
  },
  slotGroups: {
    'dividerbg.bg_image': bgImageDef,
  },
};

(DividerBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default DividerBG;
